(ns ballot-scout-v2-frontend.voter-lookup.views.filters
  (:require
   [ballot-scout-v2-frontend.components.click-outside :refer [click-outside]]
   [ballot-scout-v2-frontend.components.icon :refer [icon] :as icon]
   [ballot-scout-v2-frontend.components.link :refer [link]]
   [ballot-scout-v2-frontend.components.search :refer [clear-button]]
   [ballot-scout-v2-frontend.subs :as subs]
   [ballot-scout-v2-frontend.util :as util]
   [clojure.edn :as edn]
   [clojure.string :as str]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(defn get-status-filter-label
  "Text to display on status filter tag"
  [status]
  [:<>
   (-> status keyword util/backend-status-labels)
   (when (util/deprecated-statuses (keyword status))
     [:small {:class "has-text-warning is-size-7"} " (deprecated)"])])

(defn get-import-filter-label
  "Text to display on import filter tag"
  [import-id]
  (let [import @(rf/subscribe [::subs/import-by-id import-id])]
    [:<>
     "From "
     [:strong {:class "ml-1"}
      [link {:route-name :ballot-scout-v2-frontend.routes/import-manager
             :params {:partner-slug @(rf/subscribe [::subs/current-partner-slug])
                      :jurisdiction-slug @(rf/subscribe [::subs/current-jurisdiction-slug])
                      :election-slug @(rf/subscribe [::subs/current-election-slug])
                      :import-id import-id}}
       (str "import completed on "
            (util/render-datetime-ampm (:import/completed-ts import)))]]]))

(defn get-disposition-filter-label
  "Text to display on disposition filter tag"
  [disposition-id]
  (let [disposition @(rf/subscribe [::subs/disposition-by-id disposition-id])
        dispo-route
        (if @(rf/subscribe [::subs/on-partner-admin-page?])
          :ballot-scout-v2-frontend.routes/partner-disposition-manager
          :ballot-scout-v2-frontend.routes/jurisdiction-disposition-manager)]
    [:<>
     "From "
     [:strong {:class "ml-1"}
      [link {:route-name dispo-route
             :params {:partner-slug @(rf/subscribe [::subs/current-partner-slug])
                      :jurisdiction-slug @(rf/subscribe [::subs/current-jurisdiction-slug])
                      :election-slug @(rf/subscribe [::subs/current-election-slug])
                      :disposition-id disposition-id}}
       (str "disposition processed on "
            (util/render-datetime-ampm (:disposition/completed-ts disposition)))]]]))

(defn get-location-filter-label
  "Text to display on location filter tag"
  [last-seen-city last-seen-state]
  [:<>
   "Last scanned in "
   [:strong {:class "ml-1"}
    (str last-seen-city ", " last-seen-state)]])

(defn format-iso-date
  "Turn a date string in ISO format (`YYYY-MM-DD`) to `MM/DD/YYYY` format"
  [date-string]
  (let [[year month day] (str/split date-string #"\-")]
    (str/join "/" [month day year])))

(def date-field->label-text
  "For the date field selector dropdown"
  {:created    "Created"
   :last-event "Last Updated"
   :vb-edd     "With Voter ETA"
   :ob-edd     "With Office ETA"})

(defn get-date-on-filter-label
  "Text to display on date filter ON tag"
  [date-field date]
  [:<>
   (get date-field->label-text date-field)
   " on "
   [:strong {:class "ml-1"}
    (format-iso-date date)]])

(defn get-date-before-filter-label
  "Text to display on date filter BEFORE tag"
  [date-field date]
  [:<>
   (get date-field->label-text date-field)
   " before "
   [:strong {:class "ml-1"}
    (format-iso-date date)]])

(defn get-date-after-filter-label
  "Text to display on date filter AFTER tag"
  [date-field date]
  [:<>
   (get date-field->label-text date-field)
   " after "
   [:strong {:class "ml-1"}
    (format-iso-date date)]])

(defn get-date-between-filter-label
  "Text to display on date filter BETWEEN tag"
  [date-field date1 date2]
  [:<>
   (get date-field->label-text date-field)
   " between "
   [:strong {:class "mx-1"}
    (format-iso-date date1)]
   " and "
   [:strong {:class "ml-1"}
    (format-iso-date date2)]])

(def date1 (r/atom nil))
(def date2 (r/atom nil))
(defn clear-date-values
  []
  (reset! date1 nil)
  (reset! date2 nil))

(def filter-rules
  {:status         {:label-fn (fn [{:keys [status]}] (get-status-filter-label status))
                    :clear-from-url-fn (fn [url-state] (dissoc url-state :status))}
   :import-id      {:label-fn (fn [{:keys [import-id]}] (get-import-filter-label import-id))
                    :clear-from-url-fn (fn [url-state] (dissoc url-state :import-id))}
   :disposition-id {:label-fn (fn [{:keys [disposition-id]}] (get-disposition-filter-label disposition-id))
                    :clear-from-url-fn (fn [url-state] (dissoc url-state :disposition-id))}
   #(and (:last-seen-city %) (:last-seen-state %))
   {:label-fn (fn [{:keys [last-seen-city last-seen-state]}] (get-location-filter-label last-seen-city last-seen-state))
    :clear-from-url-fn (fn [url-state] (dissoc url-state :last-seen-city :last-seen-state))}
   :created        {:label-fn (fn [{:keys [created]}] (get-date-on-filter-label :created created))
                    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :created))}
   #(and (:created-gte %) (not (:created-lte %)))
   {:label-fn (fn [{:keys [created-gte]}] (get-date-after-filter-label :created created-gte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :created-gte))}
   #(and (:created-lte %) (not (:created-gte %)))
   {:label-fn (fn [{:keys [created-lte]}] (get-date-before-filter-label :created created-lte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :created-lte))}
   #(and (:created-gte %) (:created-lte %))
   {:label-fn (fn [{:keys [created-gte created-lte]}] (get-date-between-filter-label :created created-gte created-lte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :created-gte :created-lte))}
   :last-event     {:label-fn (fn [{:keys [last-event]}] (get-date-on-filter-label :last-event last-event))
                    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :last-event))}
   #(and (:last-event-gte %) (not (:last-event-lte %)))
   {:label-fn (fn [{:keys [last-event-gte]}] (get-date-after-filter-label :last-event last-event-gte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :last-event-gte))}
   #(and (:last-event-lte %) (not (:last-event-gte %)))
   {:label-fn (fn [{:keys [last-event-lte]}] (get-date-before-filter-label :last-event last-event-lte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :last-event-lte))}
   #(and (:last-event-gte %) (:last-event-lte %))
   {:label-fn (fn [{:keys [last-event-gte last-event-lte]}] (get-date-between-filter-label :last-event last-event-gte last-event-lte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :last-event-gte :last-event-lte))}
   :vb-edd         {:label-fn (fn [{:keys [vb-edd]}] (get-date-on-filter-label :vb-edd vb-edd))
                    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :vb-edd))}
   #(and (:vb-edd-gte %) (not (:vb-edd-lte %)))
   {:label-fn (fn [{:keys [vb-edd-gte]}] (get-date-after-filter-label :vb-edd vb-edd-gte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :vb-edd-gte))}
   #(and (:vb-edd-lte %) (not (:vb-edd-gte %)))
   {:label-fn (fn [{:keys [vb-edd-lte]}] (get-date-before-filter-label :vb-edd vb-edd-lte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :vb-edd-lte))}
   #(and (:vb-edd-gte %) (:vb-edd-lte %))
   {:label-fn (fn [{:keys [vb-edd-gte vb-edd-lte]}] (get-date-between-filter-label :vb-edd vb-edd-gte vb-edd-lte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :vb-edd-gte :vb-edd-lte))}
   :ob-edd         {:label-fn (fn [{:keys [ob-edd]}] (get-date-on-filter-label :ob-edd ob-edd))
                    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :ob-edd))}
   #(and (:ob-edd-gte %) (not (:ob-edd-lte %)))
   {:label-fn (fn [{:keys [ob-edd-gte]}] (get-date-after-filter-label :ob-edd ob-edd-gte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :ob-edd-gte))}
   #(and (:ob-edd-lte %) (not (:ob-edd-gte %)))
   {:label-fn (fn [{:keys [ob-edd-lte]}] (get-date-before-filter-label :ob-edd ob-edd-lte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :ob-edd-lte))}
   #(and (:ob-edd-gte %) (:ob-edd-lte %))
   {:label-fn (fn [{:keys [ob-edd-gte ob-edd-lte]}] (get-date-between-filter-label :ob-edd ob-edd-gte ob-edd-lte))
    :clear-from-url-fn (fn [url-state] (clear-date-values) (dissoc url-state :ob-edd-gte :ob-edd-lte))}})

(defn applied-filter-element
  "A filter tag element with an X button to clear the filter from the URL"
  [label lookup-route get-lookup-route-params clear-from-url-fn]
  (let [url-state (rf/subscribe [::subs/query-params])]
    [:div {:class "button-in mx-2 my-1"}
     [:a {:class "button is-rounded is-primary is-light is-outlined is-6 pr-6"}
      label]
     [clear-button #(rf/dispatch [:ballot-scout-v2-frontend.routes/navigate
                                  lookup-route
                                  (get-lookup-route-params)
                                  (clear-from-url-fn @url-state)])
      "has-text-primary"]]))

(defn get-applied-filter-elements
  "Parse the URL and return a sequence of filter tag elements"
  [lookup-route get-lookup-route-params]
  (let [url-state @(rf/subscribe [::subs/query-params])
        basic-filter-elements
        (reduce-kv
         (fn [elements* condition {:keys [label-fn clear-from-url-fn]}]
           (if (condition url-state)
             (conj elements* [applied-filter-element (label-fn url-state) lookup-route get-lookup-route-params clear-from-url-fn])
             elements*))
         []
         filter-rules)
        or-filters (-> url-state :or edn/read-string)
        basic-and-or-filter-elements
        (reduce
         (fn [elements* or-filter]
           (if-let [{:keys [label-fn]} (-> or-filter keys first filter-rules)]
             (let [clear-from-url-fn (fn [url-state]
                                       (let [new-or-filters (remove #(= % or-filter) or-filters)
                                             to-merge (cond
                                                        (< 1 (count new-or-filters))
                                                        {:or (str new-or-filters)}

                                                        (= 1 (count new-or-filters))
                                                        (first new-or-filters))]
                                         (-> url-state
                                             (dissoc :or)
                                             (merge to-merge))))]
               (conj elements* [applied-filter-element (label-fn or-filter) lookup-route get-lookup-route-params clear-from-url-fn]))
             elements*))
         basic-filter-elements
         or-filters)]
    basic-and-or-filter-elements))

(defn applied-filters
  "Return filter tag elements and a clear button that will remove them all from the URL"
  [lookup-route get-lookup-route-params]
  (when-let [applied-filters (not-empty (get-applied-filter-elements lookup-route get-lookup-route-params))]
    [:div {:class "applied-filters mb-3"}
     [:strong "Showing ballots:"]
     [:div {:class "is-flex is-align-items-center is-flex-wrap-wrap mt-2"}
      (into [:<>] applied-filters)
      [:a {:class "has-text-grey ml-2"
           :on-click #(do (clear-date-values)
                          (rf/dispatch [:ballot-scout-v2-frontend.routes/navigate
                                        lookup-route
                                        (get-lookup-route-params)]))}
       "Clear all"]]]))

(def backend-statuses
  [:disposition/created
   :disposition/mailed
   :voter-bound/in-transit
   :voter-bound/out-for-delivery
   :voter-bound/delivered
   :office-bound/in-transit
   :office-bound/out-for-delivery
   :office-bound/delivered
   :usps/returned
   :disposition/received
   :disposition/accepted
   :disposition/flagged])

(defn search-with-status-filters
  "Given a set of ballot status keywords, add those statuses to our search filters."
  [lookup-route get-lookup-route-params status-filters]
  (let [url-state (rf/subscribe [::subs/query-params])
        to-merge (cond
                   (< 1 (count status-filters))
                   {:or (str (map (fn [status] {:status (util/ns-keyword->str status)}) status-filters))}

                   (= 1 (count status-filters))
                   {:status (util/ns-keyword->str (first status-filters))})
        new-state (-> @url-state
                      (dissoc :page :page-size :status :or)
                      (merge to-merge))]
    (rf/dispatch [:ballot-scout-v2-frontend.routes/navigate
                  lookup-route
                  (get-lookup-route-params)
                  new-state])))

(defn fontawesome-checkbox
  "Fancier checkbox using FontAwesome icons"
  [label checked? on-click]
  [:label {:class "fa-checkbox px-2 py-1"
           :on-click on-click}
   [:input {:type "checkbox"
            :checked checked?
            :read-only true}]
   [icon ::icon/square-check {:class "has-text-primary mr-3 checked"}]
   [icon ::icon/square {:class "has-text-primary mr-3 unchecked" :type ::icon/regular}]
   label])

#_:clj-kondo/ignore
(defn status-filter-dropdown
  [lookup-route get-lookup-route-params]
  (let [active? (r/atom false)]
    (fn [lookup-route get-lookup-route-params]
      (let [{:keys [status] :as state} @(rf/subscribe [::subs/query-params])
            or-filters (edn/read-string (:or state))
            statuses-currently-applied
            (->> backend-statuses
                 (filter #(or (= (util/ns-keyword->str %) status)
                              (contains? (set or-filters) {:status (util/ns-keyword->str %)})))
                 set)
            selected-statuses (r/atom statuses-currently-applied)]
        [click-outside
         {:on-click-outside #(reset! active? false)}
         (fn [ref]
           [:div {:ref ref :class "dropdown"}
            [:div {:class ["navbar-item" "has-dropdown" (when @active? "is-active")]}
             [:a {:class "dropdown-text"
                  :on-click #(swap! active? not)}
              (let [applied-statuses-count (count statuses-currently-applied)]
                (cond
                  (= applied-statuses-count 0) "Select Ballot Statuses"
                  (= applied-statuses-count 1) "1 Status Applied"
                  :else                         (str applied-statuses-count " Statuses Applied")))
              [icon (if @active? ::icon/chevron-up ::icon/chevron-down) {:class "ml-2"}]]
             (when @active?
               [:div {:class ["navbar-dropdown" "dropdown-content"]}
                [:div {:class ["scrollable-content" "ignore-click-outside"]}
                 (into [:<>]
                       (for [backend-status backend-statuses]
                         (let [selected? (contains? @selected-statuses backend-status)]
                           [:div
                            [fontawesome-checkbox
                             (util/backend-status->label backend-status)
                             selected?
                             #(if selected?
                                (swap! selected-statuses disj backend-status)
                                (swap! selected-statuses conj backend-status))]])))]
                [:a {:class "button is-primary mt-2"
                     :on-click #(do (reset! active? false)
                                    (search-with-status-filters lookup-route get-lookup-route-params @selected-statuses))}
                 "Apply"]])]])]))))

#_:clj-kondo/ignore
(defn simple-selector-dropdown
  "Given a map of option keys and option label values, `options`, and a reagent atom `ratom`,
   create a simple dropdown element that will display the options and set the atom based on
   what the user clicks"
  [options ratom]
  (let [active? (r/atom false)]
    (fn [options ratom]
      [click-outside
       {:on-click-outside #(reset! active? false)}
       (fn [ref]
         [:div {:ref ref :class "dropdown"}
          [:div {:class ["navbar-item" "has-dropdown" (when @active? "is-active")]}
           [:a {:class "dropdown-text"
                :on-click #(swap! active? not)}
            (get options @ratom)
            [icon (if @active? ::icon/chevron-up ::icon/chevron-down) {:class "ml-2"}]]
           (when @active?
             [:div {:class ["navbar-dropdown" "dropdown-content p-0"]}
              [:div {:class ["scrollable-content" "ignore-click-outside"]}
               (into [:<>]
                     (for [[field title] options]
                       (let [selected? (= field @ratom)]
                         [:div {:class ["option" "is-size-6" "is-fullwidth" "has-hover-bg-primary-light"
                                        (if selected? "has-text-white" "has-text-black")
                                        (when selected? "has-background-primary")
                                        "p-2"]
                                :on-click #(do (reset! active? false)
                                               (reset! ratom field))}
                          title])))]])]])])))

(def date-field->title
  {:created    "Ballots Created"
   :last-event "Ballots Last Updated"
   :vb-edd     "Ballots with Voter ETA"
   :ob-edd     "Ballots with Office ETA"})

(def date-operator->title
  {:after   "After"
   :before  "Before"
   :between "Between"
   :on      "On"})

(defn add-suffix-to-field
  [field-kw suffix]
  (->> [(name field-kw) suffix] (str/join "-") keyword))

(defn get-date-query-params
  [field-kw operator date1 date2]
  (cond
    (= operator :on)
    {field-kw date1}

    (= operator :before)
    {(add-suffix-to-field field-kw "lte") date1}

    (= operator :after)
    {(add-suffix-to-field field-kw "gte") date1}

    (= operator :between)
    {(add-suffix-to-field field-kw "gte") date1
     (add-suffix-to-field field-kw "lte") date2}))

(defn search-with-date-filters
  "Given a map of ballot date filters, perform a search."
  [lookup-route get-lookup-route-params date-filters]
  (let [url-state (rf/subscribe [::subs/query-params])
        new-state (-> @url-state
                      (dissoc :page :page-size
                              :created :created-lte :created-gte
                              :last-event :last-event-lte :last-event-gte
                              :vb-edd :vb-edd-lte :vb-edd-gte
                              :ob-edd :ob-edd-lte :ob-edd-gte)
                      (merge date-filters))]
    (rf/dispatch [:ballot-scout-v2-frontend.routes/navigate
                  lookup-route
                  (get-lookup-route-params)
                  new-state])))

#_:clj-kondo/ignore
(defn date-range-filters
  [lookup-route get-lookup-route-params]
  (let [date-field-ratom (r/atom :created)
        date-operator-ratom (r/atom :after)]
    (fn [lookup-route get-lookup-route-params]
      (let [date1-populated? (-> @date1 not-empty some?)
            date2-populated? (-> @date2 not-empty some?)
            date2-before-date1? (and date1-populated? date2-populated? (< @date2 @date1))
            can-submit? (if (= :between @date-operator-ratom)
                          (and date1-populated? date2-populated? (not date2-before-date1?))
                          date1-populated?)]
        [:div {:class ["is-flex is-align-content-center"]}
         [:div
          [simple-selector-dropdown date-field->title date-field-ratom]]
         [:div {:class "ml-4 operator-dropdown"}
          [simple-selector-dropdown date-operator->title date-operator-ratom]]
         [:input {:class ["ml-4" (when date1-populated? "populated")]
                  :type "date"
                  :on-change (fn [e] (reset! date1 (.. e -target -value)))
                  :value @date1}]
         (when (= @date-operator-ratom :between)
           [:<>
            [:strong {:class "is-flex is-align-items-center filter-label is-size-8 mx-2"} "and"]
            [:input {:id "date2"
                     :class [(when date2-populated? "populated") (when date2-before-date1? "has-text-danger")]
                     :type "date"
                     :on-change (fn [e] (reset! date2 (.. e -target -value)))
                     :value @date2}]])
         [:a {:class ["button" (if can-submit? "is-primary" "has-background-grey-light has-text-white") "ml-4"]
              :disabled (not can-submit?)
              :on-click #(let [date-filters (get-date-query-params @date-field-ratom @date-operator-ratom @date1 @date2)]
                           (when can-submit?
                             (search-with-date-filters lookup-route get-lookup-route-params date-filters)))}
          "Apply"]]))))

(defn filter-drawer [lookup-route get-lookup-route-params]
  [:div {:class "filter-drawer has-background-primary-light is-flex p-4 mb-3"}
   [:form
    [:label
     [:strong {:class "filter-label is-size-8"}
      "Filter by Ballot Status"]]
    [:div
     [status-filter-dropdown lookup-route get-lookup-route-params]]]

   [:form {:class "ml-6"}
    [:label
     [:strong {:class "filter-label is-size-8"}
      "Filter by Date Range"]]
    [:div
     [date-range-filters lookup-route get-lookup-route-params]]]])
